import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ScanProvider } from './contexts/ScanContext';
import Navbar from './components/Navbar';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Domains from './pages/Domains';
import Scans from './pages/Scans';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import { useLocation } from 'react-router-dom';


import WhitelistManagement from './pages/WhitelistManagement';
import VerifyEmail from './pages/VerifyEmail';
import VerifyAdmin from './pages/VerifyAdmin';
import VerifyDomain from './pages/VerifyDomain';
import BlogArticle from './pages/BlogArticle';
import LegalDocument from './pages/LegalDocument';
import Contact from './pages/Contact';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import LoadingSpinner from './components/LoadingSpinner';
import TyposquattingAnalysis from './pages/TyposquattingAnalysis';
import Enterprise from './pages/Enterprise';
import SOP from './pages/SOP';
import Glossary from './pages/Glossary';
import UseCases from './pages/UseCases';

// Helper to preserve query params during redirect
function RedirectToDashboard() {
  const location = useLocation();
  return <Navigate to={`/dashboard${location.search}`} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const trackVisit = () => {
      // Check if Umami is loaded
      if (window.umami) {
        // Use the captured variable from index.html OR the current location
        const query = window.INITIAL_SEARCH || window.location.search;
        const url = window.location.pathname + query;

        // Send to Umami (Silent, no console logs)
        window.umami.track(props => ({ ...props, url: url }));
      } else {
        // Retry shortly if script isn't ready
        setTimeout(trackVisit, 500);
      }
    };

    trackVisit();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-dark-900 ${user ? 'pt-16' : ''}`}>
      {user && <Navbar />}
      <Routes>
        <Route path="/" element={user ? <RedirectToDashboard /> : <Landing />} />
        <Route path="/login" element={user ? <RedirectToDashboard /> : <Login />} />
        <Route path="/auth/verify" element={<VerifyEmail />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-admin" element={
          <ProtectedRoute>
            <VerifyAdmin />
          </ProtectedRoute>
        } />
        <Route path="/verify-domain" element={<VerifyDomain />} />
        <Route path="/verify-domain/:token" element={
          <ProtectedRoute>
            <VerifyDomain />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/domains" element={
          <ProtectedRoute>
            <Domains />
          </ProtectedRoute>
        } />
        <Route path="/scans" element={
          <ProtectedRoute>
            <Scans />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/reports/:scanId" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/whitelist" element={
          <ProtectedRoute>
            <WhitelistManagement />
          </ProtectedRoute>
        } />
        <Route path="/contact" element={
          <ProtectedRoute>
            <Contact />
          </ProtectedRoute>
        } />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/analysis" element={<TyposquattingAnalysis />} />
        <Route path="/enterprise" element={<Enterprise />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/sop" element={
          <ProtectedRoute>
            <SOP />
          </ProtectedRoute>
        } />

        {/* Legal Document Routes */}
        <Route path="/legal/:documentType" element={<LegalDocument />} />

        {/* Payment Routes */}
        <Route path="/payment/success" element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } />
        <Route path="/payment/cancel" element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        } />
        {/* Catch-All Route for Soft 404 Prevention */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <ScanProvider>
            <Router>
              <AppContent />
            </Router>
          </ScanProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
